import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../ui/form";
import { MetadataService } from "../../services/metadataService";

const CreateCategoryForm = ({
  open,
  onClose,
  organizationId,
  onCategoryCreated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    id_prefix: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Category name is required");
      return;
    }

    if (!formData.id_prefix.trim()) {
      setError("ID prefix is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        id_prefix: formData.id_prefix.trim().toUpperCase(),
        organization_id: organizationId,
      };

      await MetadataService.createCategory(
        categoryData,
        getAccessTokenSilently
      );

      // Reset form
      setFormData({
        name: "",
        description: "",
        id_prefix: "",
      });

      onCategoryCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: "",
        description: "",
        id_prefix: "",
      });
      setError(null);
      onClose();
    }
  };

  const getPreviewText = () => {
    if (formData.name && formData.id_prefix) {
      return `${formData.id_prefix.toUpperCase()}-001: ${formData.name}`;
    }
    return null;
  };

  const previewText = getPreviewText();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Category</DialogTitle>
        </DialogHeader>

        <Form className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}

          <FormItem>
            <FormLabel>Category Name *</FormLabel>
            <FormControl>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Information Technology, Financial Controls, Operational Risk"
                disabled={submitting}
              />
            </FormControl>
          </FormItem>

          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="e.g., IT-related risks and controls including cybersecurity, infrastructure, and data management"
                disabled={submitting}
                rows={3}
              />
            </FormControl>
          </FormItem>

          <FormItem>
            <FormLabel>ID Prefix *</FormLabel>
            <FormControl>
              <Input
                value={formData.id_prefix}
                onChange={(e) =>
                  handleInputChange("id_prefix", e.target.value.toUpperCase())
                }
                placeholder="e.g., IT, FIN, OPS"
                disabled={submitting}
                maxLength={5}
              />
            </FormControl>
          </FormItem>

          {previewText && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-body-small font-medium">ID Preview:</p>
                  <p className="text-body-small font-mono bg-muted p-2 rounded">
                    {previewText}
                  </p>
                  <p className="text-caption text-muted-foreground">
                    Example of how identifiers will be generated for risks and
                    controls in this category
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">
                  Examples of Category Names and Prefixes:
                </p>
                <div className="space-y-1">
                  <p>
                    • <strong>Information Technology:</strong> IT (IT-001,
                    IT-002, ...)
                  </p>
                  <p>
                    • <strong>Financial Controls:</strong> FIN (FIN-001,
                    FIN-002, ...)
                  </p>
                  <p>
                    • <strong>Operational Risk:</strong> OPS (OPS-001, OPS-002,
                    ...)
                  </p>
                  <p>
                    • <strong>Compliance & Legal:</strong> COMP (COMP-001,
                    COMP-002, ...)
                  </p>
                  <p>
                    • <strong>Strategic Planning:</strong> STRAT (STRAT-001,
                    STRAT-002, ...)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </Form>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Creating...
              </>
            ) : (
              "Create Category"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCategoryForm;
